import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { setTariffs } from '@helper';
import { LoadProfile, Project, Scenario } from '@model';
import {
  EssService,
  GridProgramService,
  LoadProfileService,
  ProjectService,
  ScenarioService,
  TariffService,
} from '@service';

/**
 * Interface for project resolver data
 * Provides named properties instead of tuple indices for better type safety
 */
export interface ProjectResolverData {
  scenarios: Scenario[];
  project: Project;
  loadProfiles: LoadProfile[];
}

/**
 * Project resolver function (Angular 14+ functional API)
 * Replaces the deprecated class-based ProjectResolver
 * 
 * @returns Observable of tuple [Scenario[], Project, LoadProfile[]] for backward compatibility
 */
export const projectResolver: ResolveFn<[Scenario[], Project, LoadProfile[]]> = (
  route: ActivatedRouteSnapshot
) => {
  const essService = inject(EssService);
  const projectService = inject(ProjectService);
  const scenarioService = inject(ScenarioService);
  const loadProfileService = inject(LoadProfileService);
  const tariffService = inject(TariffService);
  const gridProgramService = inject(GridProgramService);

  const projectId = route.paramMap.get('projectId');

  const projectScenarios$ = scenarioService
    .getScenarios$(projectId)
    .pipe(
      mergeMap(scenarios => {
        if (!scenarios.length) {
          return of<Scenario[]>([]);
        }
        return forkJoin(
          scenarios.map(scenario => {
            // For each scenario, fetch the ders, programs, and tariffs
            const ders$ = essService.getDers$(scenario.id);
            const tariffs$ = tariffService.getTariffsNoBills$(scenario.id);
            const programs$ = gridProgramService.getSelectedPrograms$(scenario.id);

            return forkJoin([ders$, programs$, tariffs$]).pipe(
              take(1), // Complete the sub observable after getting the value
              map(result => {
                scenario.ders = result[0];
                scenario.programs = result[1];
                setTariffs(scenario, ...result[2]);
                return scenario;
              }),
            );
          }),
        );
      }),
    );

  const loadProfiles$ = loadProfileService.loadProfiles$(projectId);

  // Check if project is already cached
  const cachedProject = projectService.projects.find(p => p.id === projectId);
  const project$: Observable<Project> = cachedProject
    ? of(cachedProject)
    : projectService.getProject$(projectId);

  return forkJoin([projectScenarios$, project$, loadProfiles$]);
};
